import axios from 'axios';

import { useCallback, useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types';
import { DeleteOutline } from '@mui/icons-material';
import { CircularProgress, Dialog, DialogActions, DialogContent } from '@mui/material';

// Redux.
import { connect } from 'react-redux';

// Router.
import { useNavigate } from 'react-router-dom';

// Firebase.
import { getAnalytics, logEvent } from "firebase/analytics";

// Components.
import BottomMenu from '../components/BottomMenu';
import IconSmall from '../components/IconSmall';
import SigninRequired from '../components/SigninRequired';
import { validTokenExists } from '../firebaseHelper';
import { getFilteredTopics } from '../utils/chatUtils';
import { isChatDeleted, rememberDeletedChat } from '../services/deletedChats';


export const Chats = ({
    authenticated,
    uid,
}) => {
    const navigate = useNavigate();
    const [chats, setChats] = useState([]);
    const [popupMessage, setPopupMessage] = useState('');
    const [deletingChatIds, setDeletingChatIds] = useState(new Set());


    // For load more...
    const [lastCreated, setLastCreated] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState(false);
    const loadMoreObserverRef = useRef();
    const requestGenerationRef = useRef(0);
    const pendingLoadRef = useRef(false);

    const tryLoadMore = useCallback(async (requestLastCreated) => {
        if (pendingLoadRef.current || !authenticated) return;
        pendingLoadRef.current = true;
        const generation = requestGenerationRef.current;
        setLoading(true);
        setLoadError(false);

        const postData = {};
        if (requestLastCreated) {
            postData.lastCreated = requestLastCreated;
        }

        try {
            await validTokenExists();
            if (generation !== requestGenerationRef.current) return;
            const res = await axios.post('/chats', postData);
            if (generation !== requestGenerationRef.current) return;
            if (!Array.isArray(res.data?.chats)) throw new Error('Invalid chat history response');
            const fetchedChats = res.data.chats;
            const visibleChats = fetchedChats.filter((chat) => !isChatDeleted(uid, `1:${chat.chatId}`));
            const nextLastCreated = res.data?.lastCreated ?? null;

            setChats(previous => {
                const ids = new Set();
                return [...(requestLastCreated ? previous : []), ...visibleChats]
                    .filter(chat => !ids.has(chat.chatId) && ids.add(chat.chatId));
            });
            setLastCreated(nextLastCreated);
            setHasMore(Boolean(nextLastCreated) && nextLastCreated !== requestLastCreated);
        } catch (err) {
            if (generation !== requestGenerationRef.current) return;
            console.log(err);
            setLoadError(true);
        } finally {
            if (generation === requestGenerationRef.current) {
                pendingLoadRef.current = false;
                setLoading(false);
            }
        }
    }, [authenticated, uid]);

    useEffect(() => {
        const analytics = getAnalytics();
        logEvent(analytics, 'screen_view', {
            firebase_screen: 'chats'
        });
    }, []);

    useEffect(() => {
        requestGenerationRef.current += 1;
        pendingLoadRef.current = false;
        setChats([]);
        setLastCreated(null);
        setHasMore(false);
        setLoading(false);
        if (authenticated === true) {
            tryLoadMore(null);
        }
        return () => { requestGenerationRef.current += 1; };
    }, [authenticated, uid, tryLoadMore]);

    useEffect(() => {
        if (!loadMoreObserverRef.current || !hasMore || loading || loadError) {
            return;
        }

        let active = true;
        const observer = new IntersectionObserver(
            (entries) => {
                if (active && entries.some(entry => entry.isIntersecting)) {
                    active = false;
                    observer.disconnect();
                    tryLoadMore(lastCreated);
                }
            },
            {
                root: null,
                rootMargin: '20px',
            }
        );

        observer.observe(loadMoreObserverRef.current);

        return () => { active = false; observer.disconnect(); };
    }, [hasMore, lastCreated, loading, loadError, tryLoadMore]);

    const chatClick = (chatId) => () => {
        navigate(`/chat/${chatId}`);
    }

    const deleteClick = (chatId) => async (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (deletingChatIds.has(chatId)) {
            return;
        }

        setDeletingChatIds((prevChatIds) => {
            const nextChatIds = new Set(prevChatIds);
            nextChatIds.add(chatId);
            return nextChatIds;
        });

        try {
            await validTokenExists();
            const res = await axios.post('/chat_delete', { "chatId": chatId });
            if (res.status === 200 && !res.data?.error) {
                rememberDeletedChat(uid, `1:${chatId}`);
                setChats((prevChats) => prevChats.filter((chat) => chat.chatId !== chatId));
            } else {
                setPopupMessage('There was an error deleting this chat.');
            }
        } catch {
            setPopupMessage('There was an error deleting this chat.');
        } finally {
            setDeletingChatIds((prevChatIds) => {
                const nextChatIds = new Set(prevChatIds);
                nextChatIds.delete(chatId);
                return nextChatIds;
            });
        }
    }

    const getChatTopicsText = (topics) => {
        const filteredTopics = getFilteredTopics(topics);
        return filteredTopics.slice(0, 2).join(', ');
    }

    if (authenticated !== true) {
        return <SigninRequired authenticated={authenticated} />;
    } else {
        return (
            <div className="container hasBottomMenu appScreen">
                <BottomMenu />
                <div>
                    <div style={{ padding: '20px' }}>
                        <span className='largeHeading'>History</span>
                    </div>

                    {loading && chats.length === 0 &&
                        <div style={{ textAlign: 'center', padding: '0px 20px' }}>
                            <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
                        </div>
                    }

                    {chats.length === 0 && !loading && !hasMore && !loadError &&
                        <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--app-text-secondary)' }}>
                            <div style={{ marginBottom: '10px' }}>
                                <IconSmall />
                            </div>
                            <button onClick={() => navigate('/ask')} style={{ fontSize: '15px', fontWeight: '500' }}>
                                Start a new chat.
                            </button>
                        </div>
                    }

                    <div style={{ padding: '0px 20px 20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {chats.map((chat) => (
                            <div key={chat.chatId} className='chatRow'>
                                <button onClick={chatClick(chat.chatId)} className='chatRowMainButton clampSafeButton'>
                                    <div>
                                        <div className='chatRowTextContainer'>
                                            {!!chat.topics && chat.topics.length > 0 &&
                                                <div className='chatRowTopics max2lines'>
                                                    {getChatTopicsText(chat.topics)}
                                                </div>
                                            }
                                            <div className='chatRowQuery max2lines'>
                                                {chat.query || 'missing query'}
                                            </div>
                                            
                                        </div>
                                    </div>
                                </button>
                                <button onClick={deleteClick(chat.chatId)} className='chatRowDeleteButton' aria-label='Delete chat' disabled={deletingChatIds.has(chat.chatId)}>
                                    {deletingChatIds.has(chat.chatId) ?
                                        <CircularProgress size={18} thickness={5} /> :
                                        <DeleteOutline style={{ fontSize: '20px' }} />
                                    }
                                </button>
                            </div>
                        ))}
                    </div>



                    {(hasMore || loadError) && (
                        <div ref={loadMoreObserverRef} style={{ textAlign: 'center', minHeight: '44px', padding: '12px 20px' }}>
                            {loadError ? <div role='alert'>
                                <p>Couldn't load chats.</p>
                                <button type='button' onClick={() => tryLoadMore(lastCreated)}>Try again</button>
                            </div> : loading && (
                                <div className="lds-ellipsis"><div></div><div></div><div></div><div></div></div>
                            )}
                        </div>
                    )}
                </div>


                <Dialog open={!!popupMessage} onClose={() => setPopupMessage('')}>
                    <DialogContent>
                        {popupMessage}
                    </DialogContent>
                    <DialogActions>
                        <button onClick={() => setPopupMessage('')} style={{ padding: '8px 14px' }}>
                            OK
                        </button>
                    </DialogActions>
                </Dialog>


            </div>
        );
    }
}

Chats.propTypes = {
    authenticated: PropTypes.bool
}

Chats.defaultProps = {

}

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    uid: state.user.uid,
})

const mapActionsToProps = {
}

const AccountChats = (props) => <Chats key={props.uid || 'guest'} {...props} />;

export default connect(mapStateToProps, mapActionsToProps)(AccountChats);
